// frontend/src/components/CartoesAnalytics/ParcelamentosFuturosChart.jsx
//
// "Até quando tenho compras parceladas, e quanto." Duas visões sobre o mesmo dado:
// por mês (quanto de parcela cai em cada mês à frente, empilhado por cartão) e
// por compra (a linha do tempo de cada parcelamento, que mostra qual compra está
// esticando o prazo).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card, Row, Col, Segmented, Select, Space, Typography, Alert,
    Tooltip, message, Empty, Spin,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RTooltip, Legend, Cell,
} from 'recharts';
import { getCartaoParcelamentosFuturos, getCartoes } from '../../services/cardService';
import { chartPalette, brandColors } from '../../styles/brandColors';
import { getErrorMessage } from '../../utils/errorHandler';
import KpiCard from './KpiCard';
import './CartoesAnalytics.css';

const { Text } = Typography;

const fmtBRL = (v) =>
    parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtCompacto = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1000) {
        const milhares = (n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
        return `R$ ${milhares}k`;
    }
    return `R$ ${Math.round(n)}`;
};

// Cor por entidade (id do cartão), nunca por posição no ranking: um filtro que muda
// a quantidade de séries não pode repintar as que sobraram.
const corDoCartao = (cartaoId, ordemIds) => {
    const idx = ordemIds.indexOf(cartaoId);
    return chartPalette[(idx < 0 ? 0 : idx) % chartPalette.length];
};

const MAX_COMPRAS_TIMELINE = 15;

const TooltipMes = ({ active, payload, label, nomePorId }) => {
    if (!active || !payload?.length) return null;
    const itens = payload.filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
    if (!itens.length) return null;
    const total = itens.reduce((acc, p) => acc + p.value, 0);
    return (
        <div className="cartoes-analytics-tooltip">
            <div className="cartoes-analytics-tooltip-titulo">{label}</div>
            {itens.map((p) => (
                <div key={p.dataKey} className="cartoes-analytics-tooltip-linha">
                    <span className="cartoes-analytics-tooltip-bullet" style={{ background: p.color }} />
                    <span className="cartoes-analytics-tooltip-rotulo">
                        {nomePorId[p.dataKey.replace('c_', '')] || 'Cartão'}
                    </span>
                    <span>{fmtBRL(p.value)}</span>
                </div>
            ))}
            {itens.length > 1 && (
                <div className="cartoes-analytics-tooltip-total">
                    <span>Total do mês</span>
                    <span>{fmtBRL(total)}</span>
                </div>
            )}
        </div>
    );
};

const TooltipCompra = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0]?.payload;
    if (!item) return null;
    return (
        <div className="cartoes-analytics-tooltip">
            <div className="cartoes-analytics-tooltip-titulo">{item.descricao}</div>
            <div className="cartoes-analytics-tooltip-linha">
                <span className="cartoes-analytics-tooltip-rotulo">Cartão</span>
                <span>{item.cartao_nome}</span>
            </div>
            <div className="cartoes-analytics-tooltip-linha">
                <span className="cartoes-analytics-tooltip-rotulo">Período</span>
                <span>{item.inicio.label} → {item.fim.label}</span>
            </div>
            <div className="cartoes-analytics-tooltip-linha">
                <span className="cartoes-analytics-tooltip-rotulo">Parcela</span>
                <span>{fmtBRL(item.valor_parcela)}</span>
            </div>
            <div className="cartoes-analytics-tooltip-linha">
                <span className="cartoes-analytics-tooltip-rotulo">Faltam</span>
                <span>{item.parcelas_restantes} de {item.total_parcelas}x</span>
            </div>
            <div className="cartoes-analytics-tooltip-total">
                <span>Restante</span>
                <span>{fmtBRL(item.valor_restante)}</span>
            </div>
        </div>
    );
};

const ParcelamentosFuturosChart = ({ cartaoIdFixo = null, isMobile = false }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [visao, setVisao] = useState('mes');
    const [janela, setJanela] = useState(24);
    const [cartaoSel, setCartaoSel] = useState('todos');
    const [messageApi, contextHolder] = message.useMessage();

    const cartaoFiltro = cartaoIdFixo || (cartaoSel === 'todos' ? null : cartaoSel);

    const fetchDados = useCallback(async () => {
        setLoading(true);
        try {
            const resposta = await getCartaoParcelamentosFuturos({
                cartoes: cartaoFiltro ? [cartaoFiltro] : undefined,
                meses: janela,
            });
            setData(resposta);
        } catch (err) {
            messageApi.error(getErrorMessage(err, 'Não foi possível carregar os parcelamentos.'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [cartaoFiltro, janela, messageApi]);

    useEffect(() => {
        fetchDados();
    }, [fetchDados]);

    // Lista completa de cartões para o seletor e para fixar a cor de cada um: sem
    // isso o seletor perderia opções ao filtrar, e a cor mudaria conforme a seleção.
    const [todosCartoes, setTodosCartoes] = useState([]);
    useEffect(() => {
        if (cartaoIdFixo) return;
        let ativo = true;
        getCartoes()
            .then((lista) => { if (ativo) setTodosCartoes(lista || []); })
            .catch(() => {});
        return () => { ativo = false; };
    }, [cartaoIdFixo]);

    const cartoes = useMemo(() => data?.cartoes || [], [data]);
    const ordemIds = useMemo(
        () => (todosCartoes.length ? todosCartoes : cartoes).map((c) => c.id),
        [todosCartoes, cartoes],
    );
    const nomePorId = useMemo(
        () => Object.fromEntries(cartoes.map((c) => [String(c.id), c.nome])),
        [cartoes],
    );

    const dadosMes = useMemo(() => (data?.meses || []).map((m) => {
        const linha = { label: m.label, vencido: m.vencido, total: m.total };
        cartoes.forEach((c) => {
            linha[`c_${c.id}`] = m.por_cartao?.[String(c.id)] || 0;
        });
        return linha;
    }), [data, cartoes]);

    const dadosCompra = useMemo(() => {
        const meses = data?.meses || [];
        if (!meses.length) return [];
        const indicePorChave = {};
        meses.forEach((m, i) => { indicePorChave[`${m.ano}-${m.mes}`] = i; });
        return (data?.compras || []).slice(0, MAX_COMPRAS_TIMELINE).map((compra) => {
            const iIni = indicePorChave[`${compra.inicio.ano}-${compra.inicio.mes}`] ?? 0;
            const iFim = indicePorChave[`${compra.fim.ano}-${compra.fim.mes}`] ?? meses.length - 1;
            return {
                ...compra,
                offset: iIni,
                duracao: Math.max(iFim - iIni + 1, 1),
                rotulo: compra.descricao.length > 28
                    ? `${compra.descricao.slice(0, 27)}…`
                    : compra.descricao,
            };
        });
    }, [data]);

    const totais = data?.totais;
    const temDados = (data?.compras || []).length > 0;
    const seriesVisiveis = cartoes.filter((c) => c.total_restante > 0);
    const mostraLegenda = seriesVisiveis.length > 1;

    const comprasOcultas = Math.max((data?.compras || []).length - MAX_COMPRAS_TIMELINE, 0);

    const controles = (
        <Space wrap size={8}>
            {!cartaoIdFixo && (
                <Select
                    value={cartaoSel}
                    onChange={setCartaoSel}
                    style={{ minWidth: 160 }}
                    options={[
                        { value: 'todos', label: 'Todos os cartões' },
                        ...todosCartoes.map((c) => ({ value: c.id, label: c.nome })),
                    ]}
                />
            )}
            <Segmented
                value={visao}
                onChange={setVisao}
                options={[
                    { label: 'Por mês', value: 'mes' },
                    { label: 'Por compra', value: 'compra' },
                ]}
            />
            <Segmented
                value={janela}
                onChange={setJanela}
                options={[
                    { label: '12m', value: 12 },
                    { label: '24m', value: 24 },
                    { label: '36m', value: 36 },
                ]}
            />
        </Space>
    );

    return (
        <div className="cartoes-analytics">
            {contextHolder}

            <Row gutter={[12, 12]} className="cartoes-analytics-kpi-row">
                <KpiCard
                    titulo={
                        <Space size={4}>
                            Comprometido
                            <Tooltip title="Soma das parcelas que ainda vão ser cobradas, sem contar faturas já vencidas.">
                                <InfoCircleOutlined style={{ color: brandColors.accent }} />
                            </Tooltip>
                        </Space>
                    }
                    valor={totais?.total_a_vencer || 0}
                    moeda
                    cor={brandColors.danger}
                    loading={loading}
                    isMobile={isMobile}
                />
                <KpiCard
                    titulo="Livre em"
                    valor={totais?.ultima_parcela?.label || '—'}
                    legenda={totais?.meses_restantes > 0
                        ? `em ${totais.meses_restantes} ${totais.meses_restantes === 1 ? 'mês' : 'meses'}`
                        : null}
                    loading={loading}
                    isMobile={isMobile}
                />
                <KpiCard
                    titulo="Média por mês"
                    valor={totais?.media_mensal || 0}
                    moeda
                    loading={loading}
                    isMobile={isMobile}
                />
                <KpiCard
                    titulo="Compras ativas"
                    valor={totais?.compras_ativas || 0}
                    loading={loading}
                    isMobile={isMobile}
                />
            </Row>

            {totais?.total_vencido > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`${fmtBRL(totais.total_vencido)} em parcelas de faturas já vencidas`}
                    description="Esses valores aparecem nos meses passados do gráfico e ficam fora da média por mês, que considera só o que ainda está por vir."
                />
            )}

            <Card
                size="small"
                className="cartoes-analytics-chart-card"
                title={visao === 'mes' ? 'Parcelas por mês' : 'Até quando vai cada compra'}
                extra={!isMobile ? controles : null}
                style={{ marginBottom: 16 }}
            >
                {isMobile && <div style={{ marginBottom: 12 }}>{controles}</div>}

                <Spin spinning={loading}>
                    {!temDados && !loading ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Nenhuma compra parcelada em aberto nos cartões."
                        />
                    ) : visao === 'mes' ? (
                        <ResponsiveContainer width="100%" height={isMobile ? 260 : 320}>
                            <BarChart data={dadosMes} margin={{ top: 16, right: 8, left: 0, bottom: 8 }}>
                                <CartesianGrid stroke="#f0f0f0" vertical={false} />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 11 }}
                                    interval={isMobile ? 2 : 0}
                                    angle={isMobile ? 0 : -35}
                                    textAnchor={isMobile ? 'middle' : 'end'}
                                    height={isMobile ? 30 : 56}
                                />
                                <YAxis tickFormatter={fmtCompacto} tick={{ fontSize: 11 }} width={56} />
                                <RTooltip
                                    content={<TooltipMes nomePorId={nomePorId} />}
                                    cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                                />
                                {mostraLegenda && <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />}
                                {seriesVisiveis.map((c, idx) => (
                                    <Bar
                                        key={c.id}
                                        dataKey={`c_${c.id}`}
                                        name={c.nome}
                                        stackId="parcelas"
                                        fill={corDoCartao(c.id, ordemIds)}
                                        stroke={brandColors.surface}
                                        strokeWidth={1}
                                        radius={idx === seriesVisiveis.length - 1 ? [4, 4, 0, 0] : 0}
                                        maxBarSize={38}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <ResponsiveContainer
                            width="100%"
                            height={Math.max(220, dadosCompra.length * 32 + 48)}
                        >
                            <BarChart
                                data={dadosCompra}
                                layout="vertical"
                                margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
                            >
                                <CartesianGrid stroke="#f0f0f0" horizontal={false} />
                                <XAxis
                                    type="number"
                                    domain={[0, (data?.meses || []).length]}
                                    ticks={(data?.meses || []).map((_, i) => i)}
                                    tickFormatter={(i) => (data?.meses?.[i]?.label || '')}
                                    tick={{ fontSize: 10 }}
                                    interval={isMobile ? 3 : 1}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="rotulo"
                                    width={isMobile ? 104 : 190}
                                    tick={{ fontSize: 11 }}
                                />
                                <RTooltip content={<TooltipCompra />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                                <Bar dataKey="offset" stackId="t" fill="transparent" isAnimationActive={false} />
                                <Bar dataKey="duracao" stackId="t" radius={[4, 4, 4, 4]} maxBarSize={18}>
                                    {dadosCompra.map((item) => (
                                        <Cell key={item.id} fill={corDoCartao(item.cartao_id, ordemIds)} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Spin>

                {visao === 'compra' && comprasOcultas > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Mostrando os {MAX_COMPRAS_TIMELINE} parcelamentos que terminam mais tarde.
                        Outros {comprasOcultas} estão na tabela abaixo.
                    </Text>
                )}
            </Card>

            {temDados && cartoes.length > 0 && (
                <Card size="small" title="Por cartão" style={{ marginBottom: 16 }}>
                    <Row gutter={[12, 12]}>
                        {cartoes.map((c) => (
                            <Col xs={24} md={12} lg={8} key={c.id}>
                                <div className="cartoes-analytics-resumo">
                                    <div className="cartoes-analytics-resumo-titulo">
                                        <span
                                            className="cartoes-analytics-bullet"
                                            style={{ background: corDoCartao(c.id, ordemIds) }}
                                        />
                                        <Text strong>{c.nome}</Text>
                                    </div>
                                    <div className="cartoes-analytics-resumo-linha">
                                        <Text type="secondary">Restante</Text>
                                        <Text strong>{fmtBRL(c.total_restante)}</Text>
                                    </div>
                                    <div className="cartoes-analytics-resumo-linha">
                                        <Text type="secondary">Última parcela</Text>
                                        <Text>{c.ultima_parcela?.label || '—'}</Text>
                                    </div>
                                    <div className="cartoes-analytics-resumo-linha">
                                        <Text type="secondary">Média por mês</Text>
                                        <Text>{fmtBRL(c.media_mensal)}</Text>
                                    </div>
                                    <div className="cartoes-analytics-resumo-linha">
                                        <Text type="secondary">Compras ativas</Text>
                                        <Text>{c.compras_ativas} ({c.parcelas_restantes} parcelas)</Text>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Card>
            )}
        </div>
    );
};

export default ParcelamentosFuturosChart;
